import React, { useEffect } from "react";
import { 
    FileText, Plus, RefreshCw, Eye, Star, 
    Calendar, User, Loader2, Edit
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { 
    useLazyGetAllBlogsQuery, 
    useUpdateBlogStatusMutation, 
    useUpdateBlogFeaturedMutation 
} from "../../../redux/actions/Admin/footerManagementAdmin"; // Adjust path if needed
import { Blog } from "../../../types/Response/AdminResponse";

const BlogManagement: React.FC = () => {
    const navigate = useNavigate();
    
    // Core RTK Query Setup
    const [triggerGetBlogs, { data: blogResponse, isLoading, isError, isFetching }] = useLazyGetAllBlogsQuery();
    const [updateStatus, { isLoading: isStatusChanging }] = useUpdateBlogStatusMutation();
    const [updateFeatured, { isLoading: isFeaturedChanging }] = useUpdateBlogFeaturedMutation();

    // Safely unpack your ASP.NET Core OkObjectResult structure (.value.data)
    const blogsList = blogResponse?.value?.data ?? [];

    const fetchBlogs = () => {
        triggerGetBlogs();
    };

    useEffect(() => {
        fetchBlogs();
    }, [triggerGetBlogs]);

    const handleTogglePublish = async (id: number, currentPublishState: boolean) => {
        if (isStatusChanging) return;
        try {
            await updateStatus({ id, isPublished: !currentPublishState }).unwrap();
            fetchBlogs();
        } catch (error) {
            console.error("Failed to alter publication status:", error);
        }
    };

    const handleToggleFeatured = async (id: number, currentFeaturedState: boolean) => {
        if (isFeaturedChanging) return;
        try {
            await updateFeatured({ id, isFeatured: !currentFeaturedState }).unwrap();
        } catch (error) {
            console.error("Failed to alter featured attribute:", error);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] p-3 sm:p-5 md:p-6 lg:p-8 antialiased text-slate-800">
            <div className="max-w-7xl mx-auto space-y-6">

                {/* --- SEAMLESS RESPONSIVE HEADER CONTROLS --- */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-5 md:p-6 border-b border-slate-200 gap-4 bg-white rounded-2xl shadow-xs">
                    <div className="space-y-1 text-left w-full sm:max-w-md md:max-w-xl">
                        <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2.5">
                            <FileText className="text-[#2F5E2E] w-5 h-5 sm:w-6 sm:h-6 stroke-[2.5] shrink-0" />
                            Blogs
                        </h1>
                       
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto justify-start sm:justify-end shrink-0">
                        <button 
                            onClick={fetchBlogs}
                            disabled={isLoading || isFetching}
                            className="p-2.5 border border-slate-200 bg-white text-slate-600 rounded-xl hover:bg-slate-50 transition cursor-pointer shadow-2xs h-10 w-10 flex items-center justify-center shrink-0"
                        >
                            <RefreshCw size={14} className={isFetching ? "animate-spin text-[#2F5E2E]" : ""} />
                        </button>
                        <button
                            onClick={() => navigate("addnewBlog")}
                            className="w-full sm:w-auto inline-flex items-center justify-center h-10 gap-1.5 bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white px-4 py-2.5 rounded-xl text-xs font-bold transition shadow-sm outline-none cursor-pointer select-none"
                        >
                            <Plus size={15} />
                            <span>Write New Article</span>
                        </button>
                    </div>
                </div>

                {/* --- DYNAMIC RENDERING INTERCEPTS --- */}
                {isLoading && (
                    <div className="text-center py-20 text-xs font-semibold text-slate-400 italic flex items-center justify-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin text-[#2F5E2E]" />
                        <span>Syncing media blog indexes repositories...</span>
                    </div>
                )}

                {isError && (
                    <div className="py-12 px-4 border border-red-100 rounded-2xl bg-red-50 text-red-700 text-xs font-bold text-center">
                        Failed to read platform content entities stream logs from data pipeline.
                    </div>
                )}

                {/* --- CORE CONTENT GRID CARDS (RESPONSIVE VIEWPORT BREAKPOINTS) --- */}
                {!isLoading && !isError && (
                    blogsList.length === 0 ? (
                        <div className="bg-white border border-dashed rounded-2xl p-10 sm:p-16 text-center text-xs text-slate-400 font-medium">
                            No published educational blog articles written. Click "Write New Article" to initialize.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                            {blogsList.map((blog: Blog) => (
                                <div 
                                    key={blog.id}
                                    className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden flex flex-col justify-between group transition hover:shadow-md w-full"
                                >
                                    {/* Thumbnail Frame Overlay Canvas */}
                                    <div className="relative w-full aspect-[16/10] bg-slate-100 border-b border-slate-100 overflow-hidden shrink-0">
                                        {blog.thumbnailUrl ? (
                                            <img 
                                                src={blog.thumbnailUrl} 
                                                alt={blog.title} 
                                                className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-slate-300">
                                                <FileText size={32} />
                                            </div>
                                        )}

                                        {/* Utility badging trackers */}
                                        <div className="absolute top-3 right-3 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                            <button
                                                onClick={() => handleToggleFeatured(blog.id, blog.isFeatured)}
                                                className={`p-1.5 rounded-lg backdrop-blur-md transition shadow-xs cursor-pointer ${blog.isFeatured ? "bg-amber-500 text-white" : "bg-white/80 text-slate-400 hover:text-amber-500"}`}
                                            >
                                                <Star size={14} fill={blog.isFeatured ? "currentColor" : "none"} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Article Summary Meta Fields */}
                                    <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between space-y-4 text-left">
                                        <div className="space-y-1.5">
                                            <h3 className="text-sm sm:text-base font-black text-slate-900 tracking-tight leading-snug line-clamp-2 min-h-[40px] sm:min-h-[44px]">
                                                {blog.title}
                                            </h3>
                                            <p className="text-[11px] sm:text-xs text-slate-400 font-medium line-clamp-2 leading-relaxed">
                                                {blog.shortDescription}
                                            </p>
                                        </div>

                                        <div className="space-y-3 pt-3 border-t border-slate-100 text-[10px] sm:text-[11px] font-semibold text-slate-400">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="flex items-center gap-1 truncate"><User size={13} className="shrink-0 text-slate-400" /> {blog.authorName || "Petland Team"}</span>
                                                <span className="flex items-center gap-1 shrink-0"><Eye size={13} className="shrink-0 text-slate-400" /> {blog.viewCount || 0} reads</span>
                                            </div>

                                            <div className="flex items-center justify-between pt-1 gap-2">
                                                <span className="flex items-center gap-1 shrink-0">
                                                    <Calendar size={13} className="shrink-0 text-slate-400" /> 
                                                    {blog.publishedAt ? new Date(blog.publishedAt).toLocaleDateString() : "Draft"}
                                                </span>

                                                {/* Visibility Status Slider Toggle Switch */}
                                                <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                                                    <span className={`text-[9px] sm:text-[10px] font-black uppercase tracking-wider ${blog.isPublished ? "text-[#2F5E2E]" : "text-slate-400"}`}>
                                                        {blog.isPublished ? "Live" : "Draft"}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleTogglePublish(blog.id, blog.isPublished)}
                                                        className="relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full transition-colors duration-150 outline-none"
                                                        style={{ backgroundColor: blog.isPublished ? "#2F5E2E" : "#CBD5E1" }}
                                                    >
                                                        <span className="pointer-events-none inline-block h-2.5 w-2.5 transform rounded-full bg-white transition mt-0.5" style={{ transform: blog.isPublished ? "translateX(14px)" : "translateX(2px)" }} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Action Operations Link row */}
                                        <div className="pt-1">
                                            <button
                                                onClick={() => navigate(`addnewBlog/${blog.id}`)}
                                                className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl font-bold text-xs text-slate-700 hover:bg-slate-100 transition flex items-center justify-center gap-1.5 cursor-pointer"
                                            >
                                                <Edit size={12} className="text-[#2F5E2E]" />
                                                <span>Edit Article</span>
                                            </button>
                                        </div>
                                    </div>

                                </div>
                            ))}
                        </div>
                    )
                )}

            </div>
        </div>
    );
};

export default BlogManagement;